import {Injectable} from '@angular/core';
import {DefaultMainPage, Theme, ThemeType} from '../interfaces/common.interface';
import {cloneDeep, isObject} from 'app/core/helpers/object';
import {gql} from 'apollo-angular';
import {ApiService} from 'app/shared/modules/api/services/api.service';
import {setDashboardSettings, setDashboardSettingsVariables} from 'app/core/services/gql/setDashboardSettings';
import {BehaviorSubject, Observable} from 'rxjs';
import {CurrentUser} from 'app/core/services/auth.service';
import {map, pluck, tap} from 'rxjs/operators';
import {LocalStorageService} from 'app/core/services/local-storage.service';
import {ThemeMode} from '../../../globalTypes';

export const setDashboardSettingsMutation = gql`
    mutation setDashboardSettings($settings: JSON!) {
        setDashboardSettings(settings: $settings) {
            dashboardSettings
        }
    }
`;

export const setThemeMutation = gql`
    mutation setTheme($themeMode: ThemeMode!) {
        setTheme(themeMode: $themeMode)
    }
`;

const isBoolean = v => v === true || v === false;

@Injectable()
export class ThemeCustomizationService {
    themeBaseSettings: Theme = {
        defaultSavedReport: '',
        chartsDefaults: {
            topLimit: 10,
            hideEmptySeries: true,
            showMissingPoints: false,
        },
        selectedTheme: {
            theme: ThemeType.NEW,
            popupShown: false,
        },
        defaultMainPage: {
            page: DefaultMainPage.MONETIZATION,
            popupShown: false,
        },
        themeMode: ThemeMode.LIGHT,
    };

    settings$ = new BehaviorSubject<Theme>(cloneDeep(this.themeBaseSettings));
    selectedTheme$ = this.settings$.pipe(pluck('selectedTheme', 'theme'));

    seriesColorsExtendedList = new Map<string, string>();
    seriesColors = new Map();

    themeMode$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(Boolean(this.themeBaseSettings.themeMode));
    selectedThemeMode$ = this.themeMode$.pipe(map(x => x));

    get isSettingsThemePopupShown() {
        return this.themeBaseSettings.selectedTheme.popupShown;
    }

    get defaultSavedReport() {
        return this.themeBaseSettings.defaultSavedReport;
    }

    constructor(
        private api: ApiService,
        private localStorage: LocalStorageService
    ) {}

    init(user: CurrentUser) {
        if (!user) {
            return;
        }

        this.themeBaseSettings = this.loadSettingsTheme(user, this.themeBaseSettings);
        this.settings$.next(this.themeBaseSettings);
        this.themeMode$.next(user.themeMode === ThemeMode.DARK);
    }

    loadSettingsTheme(userData: Partial<CurrentUser> | null, defaultValue: Theme): Theme {
        const result = cloneDeep(defaultValue);

        if (userData?.themeMode && typeof userData?.themeMode !== 'undefined') {
            result.themeMode = userData.themeMode;
        }

        if (!userData?.dashboardSettings || !isObject(userData.dashboardSettings)) {
            return result;
        }
        const data = userData.dashboardSettings;

        if (data.defaultSavedReport) {
            result.defaultSavedReport = data.defaultSavedReport;
        }

        // legacy
        if (typeof data.chartTopLimit === 'number') {
            result.chartsDefaults.topLimit = data.chartTopLimit;
        }
        if (isObject(data.chartsDefaults)) {
            if (isBoolean(data.chartsDefaults.hideEmptySeries)) {
                result.chartsDefaults.hideEmptySeries = data.chartsDefaults.hideEmptySeries;
            }
            if (typeof data.chartsDefaults.topLimit === 'number') {
                result.chartsDefaults.topLimit = data.chartsDefaults.topLimit;
            }
            if (isBoolean(data.chartsDefaults.showMissingPoints)) {
                result.chartsDefaults.showMissingPoints = data.chartsDefaults.showMissingPoints;
            }
        }

        if (isObject(data.selectedTheme)) {
            if (isBoolean(data.selectedTheme.popupShown)) {
                result.selectedTheme.popupShown = data.selectedTheme.popupShown;
            }
            if (Object.values(ThemeType).includes(data.selectedTheme.theme)) {
                result.selectedTheme.theme = data.selectedTheme.theme;
            }
        }

        if (isObject(data.defaultMainPage)) {
            if (isBoolean(data.defaultMainPage.popupShown)) {
                result.defaultMainPage.popupShown = data.defaultMainPage.popupShown;
            }
            if (Object.values(DefaultMainPage).includes(data.defaultMainPage.page)) {
                result.defaultMainPage.page = data.defaultMainPage.page;
            }
        }

        return result;
    }

    setDashboardSettings(settings: Partial<Theme>) {
        return this.api
            .mutate<setDashboardSettings, setDashboardSettingsVariables>({
                mutation: setDashboardSettingsMutation,
                variables: {
                    settings: {
                        ...this.themeBaseSettings,
                        ...settings,
                    },
                },
                skipBeforeMutationValidation: true,
            })
            .pipe(
                map(r => r.setDashboardSettings),
                tap(param => {
                    this.themeBaseSettings = cloneDeep(param.dashboardSettings);
                    this.settings$.next(this.themeBaseSettings);
                })
            );
    }

    setDarkThemeRubyPages(themeMode: ThemeMode): Observable<{setTheme: boolean}> {
        return this.api
            .mutate<{setTheme: boolean}>({
                mutation: setThemeMutation,
                variables: {themeMode},
                skipBeforeMutationValidation: true,
            })
            .pipe(
                tap(mode => {
                    const theme = mode.setTheme;
                    this.setThemeMode(theme ? ThemeMode.DARK : ThemeMode.LIGHT);
                })
            );
    }

    clearSeriesColorsList() {
        this.seriesColors.clear();
        this.seriesColorsExtendedList.clear();
    }

    setThemeMode(mode: ThemeMode) {
        this.themeBaseSettings.themeMode = mode;
        this.localStorage.setItem('apd_dark_theme', mode === ThemeMode.LIGHT ? '0' : '1');
        if (mode === ThemeMode.DARK) {
            document.body.parentElement.classList.add('allow-dark-theme');
        } else {
            document.body.parentElement.classList.remove('allow-dark-theme');
        }
        this.themeMode$.next(mode === ThemeMode.DARK);
        this.settings$.next(this.themeBaseSettings);
    }
}
